import {Component, OnInit} from '@angular/core';
import {formatDate} from '@angular/common';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {MdMarket} from '../../models/mdMarket';
import {DadProduct} from '../../models/dadProduct';
import * as _ from 'lodash';
import {DadProductService} from '../../services/dad-product.service';

@Component({
  selector: 'app-dad-product-crud',
  templateUrl: './dad-product-crud.component.html',
  styleUrls: ['./dad-product-product.component.scss']
})
export class DadProductCrudComponent implements OnInit {

  dadProductForm: UntypedFormGroup;
  marketsList: SelectItem[] = [];
  productTypesList: SelectItem[];
  rebillingProductTypesList: SelectItem[];
  selectedMarket: MdMarket;
  selectedDadProduct: DadProduct;
  isEditDadProduct: boolean;
  errorMessage = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dadProductService: DadProductService) {
  }

  ngOnInit() {
    this.initLocalVars();
    this.createFormGroup();
    this.initSelectLists();
    this.initialiseFormGroup();
  }

  private initialiseFormGroup() {
    _.forEach(this.config.data['markets'], (market: MdMarket) => {
      this.marketsList.push({
        label: market.name,
        value: market.name
      });
    });
    this.dadProductForm.get('market').setValue(this.selectedMarket.name);
    // tslint:disable-next-line:max-line-length
    this.dadProductForm.get('productType').setValue(this.selectedDadProduct ? this.selectedDadProduct.productType : 'Standard product code');
    this.dadProductForm.get('productCode').setValue(this.selectedDadProduct ? this.selectedDadProduct.productCode : null);
    this.dadProductForm.get('internalProductCode').setValue(this.selectedDadProduct ? this.selectedDadProduct.internalProductCode : null);
    this.dadProductForm.get('rebillingProductType').setValue(this.selectedDadProduct ? this.selectedDadProduct.rebillingProductType : 'Standard');
    this.dadProductForm.get('rebillingColor').setValue('#' + (this.selectedDadProduct ? this.selectedDadProduct.rebillingColor : 'FFFFFF'));
    this.dadProductForm.get('startDate').setValue(this.selectedDadProduct ? new Date(this.selectedDadProduct.startDate) : new Date());
  }

  private initLocalVars() {
    this.isEditDadProduct = this.config.data['isEditDadProduct'];
    this.selectedMarket = this.config.data['selectedMarket'];
    this.selectedDadProduct = this.config.data['selectedDadProduct'];
  }

  createFormGroup() {
    this.dadProductForm = this.formBuilder.group(
      {
        market: [{value: '', disabled: this.isEditDadProduct}],
        productType: [''],
        productCode: ['', Validators.required],
        internalProductCode: ['', Validators.required],
        rebillingProductType: ['', Validators.required],
        rebillingColor: ['', Validators.required],
        startDate: [new Date()],
        id: ['']
      },
    );
  }

  initSelectLists() {
    this.productTypesList = [
      {label: 'Standard product code', value: 'Standard product code'},
      {label: 'Standard POP product code', value: 'Standard POP product code'},
      {label: 'Internal product code', value: 'Internal product code'},
      {label: 'Internal POP product code', value: 'Internal POP product code'},
      {label: 'Subvendor product code', value: 'Subvendor product code'},
      {label: 'Subvendor POP product code', value: 'Subvendor POP product code'},
      {label: 'Zero Dollar product code', value: 'Zero Dollar product code'},
    ];
    this.rebillingProductTypesList = [
      {label: 'Standard', value: 'Standard'},
      {label: 'Subscriber Count', value: 'Subscriber Count'},
      {label: 'Colo / Access Count', value: 'Colo / Access Count'},
    ];
  }

  onSubmit() {
    if (!this.dadProductForm.valid) {
      return;
    }
    if (this.isEditDadProduct) {
      this.editDadProduct();
      return;
    }
    this.addDadProduct();
  }

  private addDadProduct() {
    const productCode = this.dadProductForm.get('productCode').value;
    const market = this.config.data['markets'].find((m: MdMarket) => m.name === this.dadProductForm.get('market').value);
    if (market?.dadProducts.some((product: DadProduct) => product.productCode === productCode)) {
      this.errorMessage = 'This market already has a product with this code.';
      return;
    }
    const dadProduct = {...this.dadProductForm.value, rebillingColor: this.rebillingColor(), startDate: this.startDate()};
    this.dadProductService.createDadProduct(dadProduct).subscribe({
      next: res => {
        if (!res['id']) {
          this.errorMessage = 'This market already has a product with this code.';
          return;
        }
        this.ref.close({...dadProduct, id: res['id']});
      },
      error: error => this.showError(error)
    });
  }

  private editDadProduct() {
    const dadProduct = {
      ...this.selectedDadProduct,
      market: this.selectedMarket.name,
      productType: this.dadProductForm.get('productType').value,
      startDate: this.startDate(),
      productCode: this.dadProductForm.get('productCode').value,
      internalProductCode: this.dadProductForm.get('internalProductCode').value,
      rebillingProductType: this.dadProductForm.get('rebillingProductType').value,
      rebillingColor: this.rebillingColor(),
    };
    this.dadProductService.editDadProduct(dadProduct).subscribe({
      next: () => this.ref.close(dadProduct),
      error: error => this.showError(error)
    });
  }

  private showError(error) {
    const message = error.error?.message;
    this.errorMessage = 'The product could not be saved' + (message ? ': ' + message : '. Please try again.');
  }

  private rebillingColor(): string {
    return this.dadProductForm.get('rebillingColor').value.replace('#', '').toUpperCase();
  }

  private startDate(): string {
    return formatDate(this.dadProductForm.get('startDate').value, 'yyyy-MM-dd', 'en-US');
  }
}
